<template>
    <div ref="header" class="flex flex-col md:flex-row justify-between items-start gap-4 py-6 md:pt-10 md:pb-10 sticky top-0 z-10">
        <div class="flex gap-4 grow">
            <CoachingModeMarble class="w-10 h-10" />
            <div class="flex flex-col gap-2">
                <h1 class="display-serif">{{ title ?? t("chat.rolePlaySummary.defaultTitle") }}</h1>
                <p class="body text-ink-muted">{{ subtitle ?? t("chat.rolePlaySummary.defaultSubtitle") }}</p>
            </div>
        </div>
        <div v-if="!hideCta" class="flex flex-wrap gap-3 self-center">
            <BaseButton v-if="showPracticeAgain" theme="secondary" size="sm" :disabled="practiceAgainPending" @click="handlePracticeAgain">{{
                t("chat.rolePlaySummary.practiceAgain")
            }}</BaseButton>
            <BaseButton theme="primary" size="sm" :disabled="practiceAgainPending" @click="handleResumeConversation">{{ t("chat.rolePlaySummary.continueCoaching") }}</BaseButton>
        </div>
    </div>
    <BaseTabs
        :tab-ids="tabIds"
        :labels="{ summary: t('chat.rolePlaySummary.tabs.summary'), breakdown: t('chat.rolePlaySummary.tabs.breakdown'), tips: t('chat.rolePlaySummary.tabs.tips') }"
        default-value="summary"
        list-class="sticky w-full z-10 bg-surface top-(--roleplay-header-height)"
        :style="{ '--roleplay-header-height': headerHeight }">
        <template #content-summary>
            <SummaryTab class="mt-4" :strengths="strengths" :weaknesses="weaknesses" :overall-summary="overallSummary" :summary-state="summaryState" :skipped="skipped" />
        </template>
        <template #content-breakdown><BreakdownTab :skills="skillsBreakdown" :skipped="skipped" class="mt-4" /></template>
        <template #content-tips><TipsTab :role-play-messages="rolePlayMessages" class="mt-4" /></template>
    </BaseTabs>
</template>

<script setup>
import { useElementBounding } from "@vueuse/core";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import BreakdownTab from "./BreakdownTab.vue";
import SummaryTab from "./SummaryTab.vue";
import TipsTab from "./TipsTab.vue";

const tabIds = ["summary", "breakdown", "tips"];

defineProps({
    title: { type: String, default: undefined },
    subtitle: { type: String, default: undefined },
    hideCta: { type: Boolean, default: false },
    showPracticeAgain: { type: Boolean, default: false },
    practiceAgainPending: { type: Boolean, default: false },
    skipped: { type: Boolean, default: false },
    strengths: { type: Array, required: true },
    weaknesses: { type: Array, required: true },
    summaryState: { type: Object, required: true },
    overallSummary: { type: String, default: "" },
    skillsBreakdown: { type: Array, required: true },
    rolePlayMessages: { type: Array, required: true },
});

const { t } = useI18n();

const emit = defineEmits(["resumeConversation", "practiceAgain"]);

const handleResumeConversation = () => {
    emit("resumeConversation");
};

const handlePracticeAgain = () => {
    emit("practiceAgain");
};

const header = useTemplateRef("header");
const { height } = useElementBounding(header);
const headerHeight = computed(() => height.value + "px");
</script>
